import { useState, useRef, useEffect } from 'react';
import { api, type Message } from '../utils/api';

interface ChatProps {
  userId: string;
  topic: string;
  sessionId: string | null;
  onSessionIdChange: (sessionId: string) => void;
}

export const Chat = ({ userId, topic, sessionId, onSessionIdChange }: ChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageText,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.chat(userId, messageText, sessionId || undefined, topic);

      if (!sessionId) {
        onSessionIdChange(response.sessionId);
      }

      const aiMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: response.message,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setSuggestions(response.suggestions || []);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  return (
    <div style={styles.container}>
      <div style={styles.messagesContainer}>
        {messages.length === 0 && (
          <div style={styles.emptyState}>
            <h2>Welcome to AI Study Buddy!</h2>
            <p>Ask me anything about {topic}</p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              ...styles.message,
              ...(message.role === 'user' ? styles.userMessage : styles.aiMessage),
            }}
          >
            <div style={styles.messageRole}>
              {message.role === 'user' ? 'You' : 'AI Buddy'}
            </div>
            <div style={styles.messageContent}>{message.content}</div>
          </div>
        ))}

        {isLoading && (
          <div style={{ ...styles.message, ...styles.aiMessage }}>
            <div style={styles.messageRole}>AI Buddy</div>
            <div style={styles.messageContent}>Thinking...</div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {suggestions.length > 0 && (
        <div style={styles.suggestions}>
          <div style={styles.suggestionsLabel}>Suggestions:</div>
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={styles.suggestionButton}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.inputForm}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question..."
          style={styles.input}
          disabled={isLoading}
        />
        <button type="submit" style={styles.sendButton} disabled={isLoading || !input.trim()}>
          Send
        </button>
      </form>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#f5f5f5',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  emptyState: {
    textAlign: 'center',
    marginTop: '40px',
    color: '#666',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '12px',
    maxWidth: '70%',
    wordWrap: 'break-word',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#007bff',
    color: 'white',
  },
  aiMessage: {
    alignSelf: 'flex-start',
    backgroundColor: 'white',
    color: '#333',
    border: '1px solid #ddd',
  },
  messageRole: {
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '4px',
    opacity: 0.7,
  },
  messageContent: {
    fontSize: '15px',
    lineHeight: '1.5',
  },
  suggestions: {
    padding: '12px 20px',
    borderTop: '1px solid #ddd',
    backgroundColor: 'white',
  },
  suggestionsLabel: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#666',
  },
  suggestionButton: {
    display: 'block',
    width: '100%',
    padding: '10px 12px',
    marginBottom: '8px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '14px',
    transition: 'all 0.2s',
  },
  inputForm: {
    display: 'flex',
    padding: '16px 20px',
    backgroundColor: 'white',
    borderTop: '1px solid #ddd',
    gap: '12px',
  },
  input: {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '24px',
    fontSize: '15px',
    outline: 'none',
  },
  sendButton: {
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 'bold',
  },
};
